import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";

@customElement("map-widget")
export class MapWidget extends LitElement {
    static styles = css`
        @keyframes slide-up {
            from {
                transform: translateY(100%);
            }
            to {
                transform: translateY(0);
            }
        }

        @keyframes slide-down {
            from {
                transform: translateY(0);
            }
            to {
                transform: translateY(100%);
            }
        }
        
        :host {
            position: fixed;
            inset: 0;
            z-index: 9999;
            background-color: rgba(0, 0, 0, 0.5); 
            padding: 0.5rem; 
            justify-content: center; 
            align-items: center; 
            overscroll-behavior: contain;
        }
        
        :host([data-transition="exited"]) {
            display: none;
        }
        
        :host([data-transition="entering"]) {
            display: flex;
            animation: slide-up 0.5s cubic-bezier(0.645, 0.045, 0.355, 1) forwards;
        }
        
        :host([data-transition="exiting"]) {
            display: flex;
            animation: slide-down 0.5s cubic-bezier(0.645, 0.045, 0.355, 1) forwards;                
        }
        
        :host([data-transition="entered"]) {
            display: flex;
        }
        
        :host > iframe {
            width: 100%;
            height: 100%;
            border: none;
            border-radius: 1.5rem;
            box-shadow: 0 0 1rem rgba(0, 0, 0, 0.5);
            max-width: 70rem;
            max-height: 60rem;
            background: hsl(0 0% 29%/0.9);
        }
    `;

    @property({
        reflect: true,
        attribute: "data-transition",
    })
    transition: "exited" | "exiting" | "entered" | "entering" = "exited";

    @query("iframe")
    iframe!: HTMLIFrameElement;

    constructor() {
        super();
        this.addEventListener("animationend", this.onAnimationEnd.bind(this));
        this.addEventListener("click", this.handleClose.bind(this));
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener(
            "triggermapwidget",
            this.handleTrigger.bind(this),
        );
        window.addEventListener(
            "submitmapwidget",
            this.handleAutocomplete.bind(this),
        );
        window.addEventListener("message", this.handleClose.bind(this));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener(
            "triggermapwidget",
            this.handleTrigger.bind(this),
        );
        window.removeEventListener(
            "submitmapwidget",
            this.handleAutocomplete.bind(this),
        );
        window.removeEventListener("message", this.handleClose.bind(this));
    }

    onAnimationEnd() {
        if (this.transition === "exiting") {
            this.transition = "exited";
        } else if (this.transition === "entering") {
            this.transition = "entered";
        }
    }

    handleAutocomplete(event: Event) {
        const { term } = (event as CustomEvent<{ term?: string }>).detail;
        this.open();
        this.iframe.contentWindow?.postMessage(
            { type: "autocomplete", term },
            process.env.NODE_ENV === "development"
                ? "*"
                : window.location.origin,
        );
    }

    handleClose(event: MessageEvent | MouseEvent) {
        /**
         * When user close the widget from outside the iframe post the message
         * to the iframe to run any cleanup code.
         */
        if (event instanceof MouseEvent) {
            this.close();
            this.iframe.contentWindow?.postMessage(
                {
                    type: "close",
                },
                process.env.NODE_ENV === "development"
                    ? "*"
                    : window.location.origin,
            );
        }

        if (event instanceof MessageEvent) {
            if (
                event.origin !== window.location.origin &&
                process.env.NODE_ENV !== "development"
            ) {
                return;
            }
            if (event.data.type !== "close") {
                return;
            }
            this.close();
        }
    }

    handleTrigger() {
        this.open();
    }

    close() {
        this.transition = "exiting";
    }

    open() {
        this.transition = "entering";
    }

    render() {
        return html`
            <iframe src="${process.env.NEXT_PUBLIC_SITE_URL!}/en/widget" />
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "map-widget": MapWidget;
    }
}
